import {
    FormStyle,
    getLocaleDayNames,
    getLocaleMonthNames,
    TranslationWidth
} from '@angular/common'
import {
    AfterViewInit,
    Component,
    ElementRef,
    forwardRef,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import {
    IMyDateModel,
    IMyDayLabels,
    IMyMonthLabels,
    INgxMyDpOptions,
    NgxMyDatePickerDirective
} from 'ngx-mydatepicker'
import * as vanillaTextMask from 'vanilla-text-mask/dist/vanillaTextMask.js'
import { CapitalizePipe } from '../capitalize.pipe'

@Component({
    selector: 'col-date-input',
    templateUrl: './date-input.component.html',
    styleUrls: ['./date-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateInputComponent),
            multi: true
        }
    ],
    encapsulation: ViewEncapsulation.None
})
export class DateInputComponent
    implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() name?: string
    @Input() max?: Date
    @Input() min?: Date

    @ViewChild('input') input: ElementRef
    @ViewChild(NgxMyDatePickerDirective) datepicker: NgxMyDatePickerDirective

    value: any
    disabled: boolean
    placeholder: string
    mydatepickerOptions: INgxMyDpOptions
    format: string

    private onChange: any
    private onTouched: any
    private maskedInputController: any

    constructor(@Inject(LOCALE_ID) private locale: string) {}

    ngOnInit() {
        this.init()
    }

    ngAfterViewInit() {
        this.initMask()
    }

    ngOnChanges(changes: SimpleChanges) {
        this.handleChanges(changes)
    }

    ngOnDestroy() {
        this.destroyMask()
    }

    writeValue(obj: any) {
        this.setValue(obj)
    }

    registerOnChange(fn: any) {
        this.onChange = fn
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn
    }

    setDisabledState?(isDisabled: boolean) {
        this.disabled = isDisabled
    }

    onDatepickerChange(event: IMyDateModel) {
        this.onChange(event.jsdate)
    }

    onBlur() {
        if (!this.datepicker.isDateValid()) {
            this.completeIfIsTwoDigitYear()
        }

        this.onTouched()
    }

    emitChange() {
        this.onChange(this.datepicker.isDateValid() ? this.value.jsdate : undefined)
    }

    private init() {
        this.format = 'dd/mm/yyyy'
        this.placeholder = 'DD/MM/YYYY'
        this.mydatepickerOptions = this.getDatepickerOpts()
    }

    private initMask() {
        this.maskedInputController = vanillaTextMask.maskInput({
            inputElement: this.input.nativeElement,
            mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d?/, /\d?/],
            placeholderChar: '\u2000'
        })
    }

    private getDisableSince() {
        if (this.max instanceof Date) {
            return {
                day: this.max.getDate() + 1,
                month: this.max.getMonth() + 1,
                year: this.max.getFullYear()
            }
        } else {
            return {
                day: 0,
                month: 0,
                year: 0
            }
        }
    }

    private getDisableUntil() {
        if (this.min instanceof Date) {
            return {
                day: this.min.getDate() - 1,
                month: this.min.getMonth() + 1,
                year: this.min.getFullYear()
            }
        } else {
            return {
                day: 0,
                month: 0,
                year: 0
            }
        }
    }

    private handleChanges(changes: SimpleChanges) {
        if (changes.min || changes.max) {
            this.mydatepickerOptions = this.getDatepickerOpts()
        }
    }

    private destroyMask() {
        this.maskedInputController.destroy()
    }

    private isInvalidDate(date: Date) {
        return isNaN(date.getTime())
    }

    private setValue(value: any) {
        if (value instanceof Date && !this.isInvalidDate(value)) {
            this.value = {
                date: {
                    year: value.getFullYear(),
                    month: value.getMonth() + 1,
                    day: value.getDate()
                }
            }
        } else {
            this.value = null
        }

        if (this.disabled) {
            this.datepicker.setDisabledState(false)
            this.datepicker.writeValue(this.value)
            this.datepicker.setDisabledState(true)
        }
    }

    private getDatepickerOpts(): INgxMyDpOptions {
        return {
            markCurrentDay: true,
            markWeekends: { marked: false, color: '' },
            dateFormat: this.format,
            dayLabels: this.getDayLabels(),
            monthLabels: this.getMonthLabels(),
            disableUntil: this.getDisableUntil(),
            disableSince: this.getDisableSince(),
            todayBtnTxt: 'Hoje',
            ariaLabelPrevMonth: 'Mês anterior',
            ariaLabelNextMonth: 'Próximo mês',
            ariaLabelPrevYear: 'Ano anterior',
            ariaLabelNextYear: 'Próximo ano'
        }
    }

    private getDayLabels(): IMyDayLabels {
        const pipe = new CapitalizePipe()
        const days = getLocaleDayNames(
            this.locale,
            FormStyle.Standalone,
            TranslationWidth.Abbreviated
        )

        return {
            su: pipe.transform(days[0]),
            mo: pipe.transform(days[1]),
            tu: pipe.transform(days[2]),
            we: pipe.transform(days[3]),
            th: pipe.transform(days[4]),
            fr: pipe.transform(days[5]),
            sa: pipe.transform(days[6])
        }
    }

    private getMonthLabels(): IMyMonthLabels {
        const pipe = new CapitalizePipe()

        return getLocaleMonthNames(
            this.locale,
            FormStyle.Standalone,
            TranslationWidth.Abbreviated
        ).reduce((labels, month, i) => {
            labels[i + 1] = pipe.transform(month)

            return labels
        }, {})
    }

    private completeIfIsTwoDigitYear() {
        const dateStr = this.input.nativeElement.value.trim()
        const dateParts = dateStr.split('/')
        const yearStr = dateParts[2]
        let date: Date
        let valid = true

        if (yearStr && yearStr.length === 2) {
            const year = this.getPossibleYear(yearStr)
            const month = Number.parseInt(dateParts[1]) - 1
            const day = Number.parseInt(dateParts[0])

            date = new Date(year, month, day)

            if (this.max && date > this.max) {
                valid = false
            }

            if (this.min && date < this.min) {
                valid = false
            }
        }

        if (date && valid) {
            this.setValue(date)
            this.onChange(date)
        } else {
            this.input.nativeElement.value = ''
        }
    }

    private getPossibleYear(twoDigitYear: string) {
        const currentYear = new Date().getFullYear()
        const max = currentYear + 40
        const min = currentYear - 60
        let thousendsAndHundreds = Number.parseInt(currentYear.toString().substr(0, 2))
        let possibleYear = Number.parseInt(
            `${thousendsAndHundreds}${twoDigitYear.padStart(2, '0')}`
        )

        if (min <= possibleYear && possibleYear <= max) {
            return possibleYear
        } else {
            thousendsAndHundreds--
            possibleYear = Number.parseInt(
                `${thousendsAndHundreds}${twoDigitYear.padStart(2, '0')}`
            )

            if (min <= possibleYear && possibleYear <= max) {
                return possibleYear
            }
        }

        throw new Error('Algo deu errado nesse ano digitado')
    }
}



// WEBPACK FOOTER //
// ./src/app/common/date-input/date-input.component.ts